'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { usePitchDetection } from '@/hooks/usePitchDetection';
import { ScoreViewer, ScoreViewerHandle, ScoreNoteStep } from '@/components/score/ScoreViewer';
import { usePreferences } from '@/lib/preferences';
import { useT } from '@/lib/i18n';
import { formatDetectedNote, formatScoreNote, midiToFreq } from '@/lib/notes';
import { InstrumentSampler, buildSampleList } from '@/lib/audioSampler';
import { useMetronome } from '@/hooks/useMetronome';
import { INSTRUMENTS } from '@/lib/constants';

const CENT_THRESHOLD = 50;

const INSTRUMENT_EMOJI: Record<string, string> = {
  flute: '🪈', violin: '🎻', piano: '🎹', guitar: '🎸', cello: '🎻',
  trumpet: '🎺', saxophone: '🎷', clarinet: '🎵', oboe: '🎶', voice: '🎤',
};
const DEFAULT_SOURCE = '/scores/samanyolu.mxl';

type Mode = 'idle' | 'playing' | 'recording';

export function ExercisePlayer() {
  const t = useT();
  const { prefs, setPrefs } = usePreferences();
  const { data: session } = useSession();
  const [metronomeOn, setMetronomeOn] = useState(false);
  useMetronome(prefs.bpm, metronomeOn);

  const [source, setSource] = useState<string | File | null>(
    () => prefs.selectedMxlUrl || DEFAULT_SOURCE
  );
  const [steps, setSteps] = useState<ScoreNoteStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [stepIdx, setStepIdx] = useState(0);
  const [results, setResults] = useState<Record<number, 'correct' | 'wrong'>>({});
  const [completed, setCompleted] = useState(false);
  const [fileName, setFileName] = useState<string>(() => {
    if (prefs.selectedMxlUrl) {
      return prefs.selectedMxlUrl.split('/').pop() ?? 'score.mxl';
    }
    return 'samanyolu.mxl';
  });

  // Image / PDF upload + Audiveris conversion
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string>('');
  const [converting, setConverting] = useState(false);
  const [convertStatus, setConvertStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [convertError, setConvertError] = useState<string>('');

  // Revoke previous object URL to avoid memory leaks
  const imageUrlRef = useRef<string | null>(null);

  const convertImageToMxl = async (f: File) => {
    setConverting(true);
    setConvertStatus('idle');
    setConvertError('');
    try {
      const form = new FormData();
      form.append('image', f);
      const res = await fetch('/api/convert', { method: 'POST', body: form });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        // Prefer a localized, actionable message keyed by the server's error code;
        // fall back to the raw server text.
        const codeKey = json.code ? `home.convertErr.${json.code}` : '';
        const localized = codeKey ? t(codeKey) : '';
        const msg = (localized && localized !== codeKey ? localized : '') ||
          [json.error, json.detail].filter(Boolean).join('\n');
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const { imageUrl: persistentImageUrl, mxlUrl } = await res.json() as {
        imageUrl: string;
        mxlUrl: string;
      };
      // Switch from temporary object URL to the persistent server URL
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = null;
      }
      fullReset();
      setFileName(f.name.replace(/\.[^.]+$/, '.mxl'));
      setSource(mxlUrl);
      setImageUrl(persistentImageUrl);
      setConvertStatus('ok');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setConvertStatus('error');
      setConvertError(e?.message ?? String(err));
    } finally {
      setConverting(false);
    }
  };

  const onImagePicked = (f: File | null) => {
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    }
    if (!f) {
      setImageFile(null);
      setImageUrl(null);
      setImageName('');
      setConvertStatus('idle');
      return;
    }
    const url = URL.createObjectURL(f);
    imageUrlRef.current = url;
    setImageFile(f);
    setImageUrl(url);
    setImageName(f.name);
    // Auto-convert if it's an image/PDF (not already an .mxl)
    if (!f.name.endsWith('.mxl') && !f.name.endsWith('.xml')) {
      convertImageToMxl(f);
    }
  };

  useEffect(() => () => {
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
  }, []);

  const viewerRef = useRef<ScoreViewerHandle>(null);
  const { isRunning, pitch, error: micError, start: micStart, stop: micStop } = usePitchDetection();

  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);

  const modeRef = useRef<Mode>('idle');
  const stepIdxRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const stepCorrectRef = useRef<boolean>(false);
  const samplerRef = useRef<InstrumentSampler | null>(null);
  const wasRecordingRef = useRef(false);
  const scorePostedRef = useRef(false);

  // Reinitialise sampler when instrument preference changes.
  useEffect(() => {
    samplerRef.current?.stop();
    samplerRef.current = new InstrumentSampler(buildSampleList(prefs.instrument));
    return () => {
      samplerRef.current?.stop();
    };
  }, [prefs.instrument]);

  // When selectedMxlUrl arrives from server (after login), load it if user
  // hasn't manually picked a different file yet.
  const selectedMxlApplied = useRef(false);
  useEffect(() => {
    if (selectedMxlApplied.current) return;
    if (!prefs.selectedMxlUrl) return;
    selectedMxlApplied.current = true;
    setSource(prefs.selectedMxlUrl);
    setFileName(prefs.selectedMxlUrl.split('/').pop() ?? 'score.mxl');
  }, [prefs.selectedMxlUrl]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    stepIdxRef.current = stepIdx;
  }, [stepIdx]);

  const currentStep = steps[stepIdx];

  /** ms for one step given BPM (quarter = beat). */
  const stepDurationMs = useCallback(
    (step: ScoreNoteStep | undefined) => {
      if (!step) return 0;
      const beats = step.durationWhole * 4;
      return beats * (60000 / prefs.bpm);
    },
    [prefs.bpm]
  );

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownTimerRef.current !== null) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(null);
  };

  const fullReset = useCallback(() => {
    clearTimer();
    clearCountdown();
    samplerRef.current?.stop();
    setMode('idle');
    setStepIdx(0);
    setResults({});
    setCompleted(false);
    stepCorrectRef.current = false;
    wasRecordingRef.current = false;
    scorePostedRef.current = false;
    viewerRef.current?.resetColors();
    viewerRef.current?.goTo(0);
    viewerRef.current?.hideCursor();
  }, []);

  const handleReady = useCallback((loaded: ScoreNoteStep[]) => {
    setSteps(loaded);
    setStepIdx(0);
    stepIdxRef.current = 0;
    setResults({});
    setCompleted(false);
    setLoadError(null);
    // Show cursor parked on the first note as soon as the score is ready.
    viewerRef.current?.goTo(0);
  }, []);

  const handleError = useCallback((err: string) => {
    setLoadError(err);
    setSteps([]);
  }, []);

  const finishStep = useCallback(
    (currentIdx: number, shouldRecord: boolean) => {
      if (shouldRecord) {
        const wasCorrect = stepCorrectRef.current;
        setResults((prev) => ({
          ...prev,
          [currentIdx]: wasCorrect ? 'correct' : 'wrong',
        }));
        viewerRef.current?.markStep(currentIdx, wasCorrect ? 'correct' : 'wrong');
      }
      stepCorrectRef.current = false;
    },
    []
  );

  const advanceFrom = useCallback(
    (idx: number, recording: boolean) => {
      if (modeRef.current === 'idle') return;
      const step = steps[idx];
      if (!step) return;
      const duration = stepDurationMs(step);

      // Audible playback only in Play mode (not during Record — mic would hear it).
      if (!recording && step.midi !== null) {
        samplerRef.current?.playNote(step.midi, duration);
      }

      timerRef.current = window.setTimeout(() => {
        finishStep(idx, recording);
        const next = idx + 1;
        if (next >= steps.length) {
          setCompleted(true);
          setMode('idle');
          viewerRef.current?.hideCursor();
          samplerRef.current?.stop();
          if (recording) micStop();
          return;
        }
        setStepIdx(next);
        viewerRef.current?.goTo(next);
        advanceFrom(next, recording);
      }, duration);
    },
    [steps, stepDurationMs, finishStep, micStop]
  );

  const startPlayback = useCallback(async (record: boolean) => {
    if (!steps.length) return;
    clearTimer();
    setResults({});
    setCompleted(false);
    setStepIdx(0);
    stepCorrectRef.current = false;
    wasRecordingRef.current = record;
    scorePostedRef.current = false;
    viewerRef.current?.resetColors();
    viewerRef.current?.goTo(0);
    viewerRef.current?.showCursor();
    if (record) {
      await micStart();
      setMode('recording');
      modeRef.current = 'recording';
    } else {
      // Ensure the AudioContext is resumed inside the user-gesture click handler,
      // and samples are loaded before the first note fires.
      try {
        await samplerRef.current?.resume();
        await samplerRef.current?.load();
      } catch {
        /* non-fatal: just won't hear anything */
      }
      setMode('playing');
      modeRef.current = 'playing';
    }
    advanceFrom(0, record);
  }, [steps, advanceFrom, micStart]);

  const stopAll = useCallback(() => {
    clearTimer();
    clearCountdown();
    samplerRef.current?.stop();
    setMode('idle');
    modeRef.current = 'idle';
    viewerRef.current?.hideCursor();
    micStop();
  }, [micStop]);

  /** Play the note at `idx` through the sampler (fire-and-forget). */
  const playNoteAt = useCallback(async (idx: number) => {
    const step = steps[idx];
    if (!step || step.midi == null) return;
    try {
      await samplerRef.current?.resume();
      await samplerRef.current?.load();
    } catch { /* non-fatal */ }
    samplerRef.current?.playNote(step.midi, stepDurationMs(step));
  }, [steps, stepDurationMs]);

  /** Move the cursor to `idx` without playing. */
  const moveCursorTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(steps.length - 1, idx));
    setStepIdx(clamped);
    stepIdxRef.current = clamped;
    viewerRef.current?.goTo(clamped);
  }, [steps]);

  // Right: play current note then advance cursor.
  // Left: retreat cursor then play that note.
  const stepRight = useCallback(() => {
    const curr = stepIdxRef.current;
    void playNoteAt(curr);
    moveCursorTo(curr + 1);
  }, [playNoteAt, moveCursorTo]);

  const stepLeft = useCallback(() => {
    const prev = Math.max(0, stepIdxRef.current - 1);
    moveCursorTo(prev);
    void playNoteAt(prev);
  }, [playNoteAt, moveCursorTo]);

  // Arrow key navigation (idle mode only — doesn't interfere with auto-play/record).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (modeRef.current !== 'idle') return;
      if (!steps.length) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); stepRight(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); stepLeft(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [steps, stepRight, stepLeft]);

  /**
   * Visible 4-second count-in before recording actually starts. Each tick
   * stays on screen for 1s (4 → 3 → 2 → 1), then we kick off startPlayback.
   * Cancelled by stopAll/fullReset.
   */
  const startRecordWithCountdown = useCallback(() => {
    if (!steps.length) return;
    clearTimer();
    clearCountdown();

    const tick = (remaining: number) => {
      if (remaining <= 0) {
        setCountdown(null);
        countdownTimerRef.current = null;
        // actual record starts now
        void startPlayback(true);
        return;
      }
      setCountdown(remaining);
      countdownTimerRef.current = window.setTimeout(() => {
        tick(remaining - 1);
      }, 1000);
    };
    tick(4);
  }, [steps, startPlayback]);

  useEffect(() => () => clearTimer(), []);

  // pitch matching during recording
  useEffect(() => {
    if (mode !== 'recording' || !pitch || !currentStep) return;
    if (currentStep.midi === null) return;
    const detected = formatDetectedNote(pitch.frequency, prefs.noteNaming);
    const diff = detected.midi - currentStep.midi;
    const inTune =
      diff === 0 && Math.abs(detected.cents) <= CENT_THRESHOLD;
    if (inTune) {
      stepCorrectRef.current = true;
    }
  }, [pitch, mode, currentStep, prefs.noteNaming]);

  useEffect(() => {
    if (!completed || !wasRecordingRef.current || scorePostedRef.current) return;
    if (!session?.user?.id) return;
    scorePostedRef.current = true;
    const totalSteps = steps.length;
    const correctSteps = Object.values(results).filter(r => r === 'correct').length;
    fetch('/api/scores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        songTitle: fileName.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '),
        score: correctSteps * 100,
        accuracy: totalSteps > 0 ? correctSteps / totalSteps : 0,
        correct: correctSteps,
        total: totalSteps,
      }),
    }).catch(() => {});
  }, [completed, session?.user?.id, fileName, results, steps.length]);

  const onFilePicked = (f: File | null) => {
    if (!f) return;
    setFileName(f.name);
    fullReset();
    setSource(f);
  };

  const correctCount = useMemo(
    () => Object.values(results).filter((r) => r === 'correct').length,
    [results]
  );
  const totalAnswered = Object.keys(results).length;
  const accuracy = totalAnswered > 0 ? Math.round((correctCount / totalAnswered) * 100) : 0;

  const targetLabel = currentStep
    ? formatScoreNote(currentStep.label, prefs.noteNaming)
    : '—';
  const detected = pitch ? formatDetectedNote(pitch.frequency, prefs.noteNaming) : null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('app.title')}</h1>
        <p className="text-slate-500 text-sm mt-1">{t('app.subtitle')}</p>
        <p className="text-slate-400 text-xs mt-1">{t('app.subtitleHint')}</p>
        <p className="text-violet-500 text-xs mt-0.5 font-medium">{t('app.signUp')}</p>
      </div>

      {/* Image / PDF upload */}
      <div className="bg-sky-100 rounded-2xl p-4">
        <div className="flex items-start gap-4 flex-wrap">
          <label className="flex-1 min-w-[220px] cursor-pointer">
            <span className="text-xs text-slate-500 uppercase tracking-widest">
              {t('home.uploadImage')}
            </span>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 bg-sky-200 border border-dashed border-sky-300 rounded-xl px-4 py-3 text-sm min-w-0">
                <div className="font-mono text-sky-300 truncate">
                  {imageName || t('home.uploadImageHint')}
                </div>
              </div>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={(e) => onImagePicked(e.target.files?.[0] ?? null)}
              />
              <span className="bg-sky-600 hover:bg-sky-500 text-white text-sm px-4 py-3 rounded-xl whitespace-nowrap transition-colors">
                {t('home.uploadImage').split(' ')[0]}
              </span>
            </div>
          </label>
          {imageUrl && (
            <button
              onClick={() => onImagePicked(null)}
              className="mt-6 px-4 py-2 text-xs bg-sky-200 hover:bg-sky-300 text-slate-500 hover:text-slate-900 rounded-lg transition-colors self-center"
            >
              ✕ {t('home.uploadImageClear')}
            </button>
          )}
        </div>

        {/* Conversion status */}
        {converting && (
          <div className="mt-3 flex items-center gap-2 text-sm text-sky-300">
            <svg className="animate-spin h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {t('home.converting')}
          </div>
        )}
        {!converting && convertStatus === 'ok' && (
          <div className="mt-3 text-sm text-green-400">
            ✓ {t('home.convertOk')}
          </div>
        )}
        {!converting && convertStatus === 'error' && (
          <div className="mt-3 bg-red-950 border border-red-800 text-red-300 rounded-xl p-3 text-sm">
            ✗ {t('home.convertErr')}: {convertError}
          </div>
        )}

        {/* Image / PDF preview */}
        {imageUrl && imageFile && (
          <div className="mt-4">
            {imageFile.type === 'application/pdf' ? (
              <embed
                src={imageUrl}
                type="application/pdf"
                className="w-full rounded-xl border border-sky-300"
                style={{ height: '520px' }}
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={imageUrl}
                alt={imageName}
                className="w-full rounded-xl border border-sky-300 object-contain max-h-[520px] bg-white"
              />
            )}
          </div>
        )}
      </div>

      {/* .mxl Upload + BPM */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-sky-100 rounded-2xl p-4">
          <label className="block cursor-pointer">
            <span className="text-xs text-slate-500 uppercase tracking-widest">
              {t('home.upload')}
            </span>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 bg-sky-200 border border-dashed border-sky-300 rounded-xl px-4 py-3 text-sm">
                <div className="font-mono text-violet-300 truncate">{fileName}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t('home.uploadHint')}</div>
              </div>
              <input
                type="file"
                accept=".mxl,.xml,.musicxml"
                className="hidden"
                onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
              />
              <span className="bg-violet-600 hover:bg-violet-500 text-white text-sm px-4 py-3 rounded-xl">
                {t('home.upload')}
              </span>
            </div>
          </label>
        </div>

        <div className="bg-sky-100 rounded-2xl p-4">
          <div className="flex justify-between items-baseline">
            <div className="text-xs text-slate-500 uppercase tracking-widest">
              {t('home.bpm')}
            </div>
            <div className="font-mono text-2xl font-bold text-violet-300">
              {prefs.bpm}{' '}
              <span className="text-xs text-slate-500 font-sans tracking-widest">BPM</span>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xs text-slate-500 font-mono w-6 text-right">30</span>
            <input
              type="range"
              min={30}
              max={240}
              value={prefs.bpm}
              onChange={(e) => setPrefs({ bpm: parseInt(e.target.value, 10) })}
              className="flex-1 accent-violet-500"
            />
            <span className="text-xs text-slate-500 font-mono w-6">240</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500">{t('home.metronome')}</span>
            <button
              onClick={() => setMetronomeOn(v => !v)}
              aria-label={metronomeOn ? 'Metronome off' : 'Metronome on'}
              className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                metronomeOn ? 'bg-violet-600' : 'bg-sky-300'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                metronomeOn ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Instrument picker */}
      <div className="bg-sky-100 rounded-2xl p-4">
        <span className="text-xs text-slate-500 uppercase tracking-widest">{t('profile.instrument')}</span>
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {([['', '—', t('profile.instrumentNone')]] as [string, string, string][])
            .concat(INSTRUMENTS.map(i => [i, INSTRUMENT_EMOJI[i], t(`instrument.${i}`)] as [string, string, string]))
            .map(([val, emoji, label]) => {
              const active = prefs.instrument === val;
              return (
                <button
                  key={val}
                  onClick={() => setPrefs({ instrument: val })}
                  className={`shrink-0 flex flex-col items-center gap-1 rounded-xl px-3 py-2 border transition-colors ${
                    active
                      ? 'bg-violet-600 border-violet-600 text-white'
                      : 'bg-sky-200 border-sky-300 text-slate-700 active:bg-sky-300'
                  }`}
                >
                  <span className="text-2xl leading-none">{emoji}</span>
                  <span className="text-[10px] font-medium whitespace-nowrap">{label}</span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Score */}
      <div className="relative bg-sky-100 rounded-2xl p-4">
        {loading && (
          <div className="text-sm text-slate-500 mb-2">{t('home.loading')}</div>
        )}
        {loadError && (
          <div className="bg-red-950 border border-red-800 text-red-300 rounded-xl p-3 text-sm mb-2">
            {t('home.loadError')}: {loadError}
          </div>
        )}
        {!source && (
          <div className="text-slate-500 text-sm p-6 text-center">{t('home.noScore')}</div>
        )}
        {source && (
          <ScoreViewer
            ref={viewerRef}
            source={source}
            onReady={handleReady}
            onError={handleError}
            onLoadingChange={setLoading}
          />
        )}

        {countdown !== null && (
          <div
            aria-live="polite"
            className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-sky-200/70 backdrop-blur-sm pointer-events-none z-10"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-red-300 mb-2">
              ● {t('home.countdown')}
            </div>
            <div
              key={countdown}
              className="text-[10rem] leading-none font-bold font-mono text-white drop-shadow-[0_0_30px_rgba(239,68,68,0.6)] animate-pulse"
            >
              {countdown}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3">
        {mode === 'idle' && countdown === null ? (
          <>
            <button
              onClick={() => startPlayback(false)}
              disabled={!steps.length}
              className="flex-1 min-w-[105px] bg-violet-600 hover:bg-violet-500 disabled:bg-sky-200 disabled:text-slate-400 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              {t('home.play')}
            </button>
            <button
              onClick={startRecordWithCountdown}
              disabled={!steps.length}
              className="flex-1 min-w-[105px] bg-red-600 hover:bg-red-500 disabled:bg-sky-200 disabled:text-slate-400 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              ● {t('home.record')}
            </button>
          </>
        ) : (
          <button
            onClick={stopAll}
            className="flex-1 min-w-[105px] bg-sky-300 hover:bg-sky-400 text-slate-900 font-semibold py-3 rounded-xl transition-colors"
          >
            {mode === 'recording' || countdown !== null
              ? t('home.stopRecord')
              : t('home.stop')}
          </button>
        )}
        <button
          onClick={fullReset}
          className="px-4 bg-sky-200 hover:bg-sky-300 text-slate-900 font-semibold py-3 rounded-xl transition-colors"
        >
          {t('home.reset')}
        </button>

        {/* Step-through navigation — idle only */}
        {mode === 'idle' && countdown === null && steps.length > 0 && (
          <div className="flex items-center gap-2 w-full mt-1 border-t border-sky-200 pt-3">
            <button
              onClick={stepLeft}
              disabled={stepIdx <= 0}
              aria-label="Previous note"
              className="flex-1 flex items-center justify-center gap-2 bg-sky-200 hover:bg-sky-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 font-semibold py-2.5 rounded-xl transition-colors"
            >
              <span className="text-lg">←</span>
              <span className="text-sm">{t('home.prevNote')}</span>
            </button>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => void playNoteAt(stepIdx)}
                aria-label="Play current note"
                className="w-9 h-9 flex items-center justify-center bg-violet-600 hover:bg-violet-500 text-white rounded-full shadow transition-colors"
              >
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 ml-0.5">
                  <path d="M3 2.5l10 5.5-10 5.5V2.5z"/>
                </svg>
              </button>
              <span className="text-xs text-slate-400 font-mono">{stepIdx + 1}/{steps.length}</span>
            </div>
            <button
              onClick={stepRight}
              aria-label="Next note"
              className="flex-1 flex items-center justify-center gap-2 bg-sky-200 hover:bg-sky-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 font-semibold py-2.5 rounded-xl transition-colors"
            >
              <span className="text-sm">{t('home.nextNote')}</span>
              <span className="text-lg">→</span>
            </button>
          </div>
        )}
      </div>

      {/* Live feedback */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-sky-100 rounded-2xl p-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">
                {t('home.detected')}
              </div>
              <div className="text-4xl font-bold font-mono tracking-tighter mt-1">
                {detected?.label ?? '—'}
                {detected && (
                  <span className="text-lg font-normal text-slate-500 ml-2">
                    — {pitch?.frequency} Hz
                  </span>
                )}
              </div>
              {!pitch && (
                <div className="text-sm text-slate-500 mt-1">{t('home.waiting')}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-widest">
                {t('home.target')}
              </div>
              <div className="text-lg font-semibold text-amber-400 mt-1">
                {targetLabel}
                {currentStep?.midi !== null && currentStep?.midi !== undefined && (
                  <span className="text-sm font-normal text-slate-500 ml-1">
                    — {midiToFreq(currentStep.midi)} Hz
                  </span>
                )}
              </div>
              {detected && currentStep?.midi !== null && (
                <div
                  className={`text-xs mt-1 ${
                    Math.abs(detected.cents) < CENT_THRESHOLD ? 'text-green-400' : 'text-amber-400'
                  }`}
                >
                  {detected.cents > 0 ? '+' : ''}
                  {detected.cents} ¢
                </div>
              )}
            </div>
          </div>

          <div className="relative h-2 bg-sky-200 rounded-full overflow-hidden mt-5">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-sky-400" />
            <div
              className="absolute top-0 bottom-0 w-3 rounded-full transition-all duration-75"
              style={{
                left: `${Math.max(2, Math.min(96, 50 + (detected?.cents ?? 0) / 2))}%`,
                transform: 'translateX(-50%)',
                background:
                  detected && Math.abs(detected.cents) < CENT_THRESHOLD
                    ? '#4ade80'
                    : detected && detected.cents > 0
                    ? '#fbbf24'
                    : '#60a5fa',
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>♭ {t('home.flat')}</span>
            <span>♯ {t('home.sharp')}</span>
          </div>

          {micError && (
            <div className="bg-red-950 border border-red-800 text-red-300 rounded-xl p-3 text-sm mt-4">
              {t('home.micError')}: {micError}
            </div>
          )}

          {completed && (
            <div className="bg-green-950 border border-green-800 text-green-300 rounded-xl p-4 text-center mt-4">
              <div className="text-2xl font-bold mb-1">{t('home.completed')}</div>
              <div className="text-sm">
                {correctCount} / {steps.length} · {accuracy}%
              </div>
            </div>
          )}
        </div>

        <div className="bg-sky-100 rounded-2xl p-4 grid grid-cols-2 gap-3 content-start">
          <div className="bg-sky-200 rounded-xl p-3 text-center col-span-2">
            <div className="text-5xl font-bold font-mono text-violet-400">
              {correctCount * 100}
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">
              {t('home.totalScore')}
            </div>
          </div>
          <div className="bg-sky-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{correctCount}</div>
            <div className="text-xs text-slate-500 mt-1">{t('home.correct')}</div>
          </div>
          <div className="bg-sky-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-slate-700">{accuracy}%</div>
            <div className="text-xs text-slate-500 mt-1">{t('home.accuracy')}</div>
          </div>
          <div className="col-span-2 text-xs text-slate-500 text-center">
            {isRunning ? t('home.micStop') : t('home.micStart')}
          </div>
        </div>
      </div>
    </div>
  );
}
